/**
 * The store editor's plain-language "how do shoppers pay" summary (card #666,
 * item 5). READ-ONLY and DISPLAY-ONLY: it is computed from the settings the
 * editor holds and changes nothing.
 *
 * Drift guard. A summary that disagrees with the real rules is worse than no
 * summary, so:
 *   - the payment method is classified by the SAME helpers checkout uses
 *     (storePaymentModel: isCardPaymentMethod / isPayrollPaymentMethod);
 *   - every sentence here says only what the owner docs state the money code
 *     guarantees (card: allowance applied, rest on the card; payroll: the
 *     allowance is a firm limit and no card is taken; Net: an order placed
 *     now and invoiced to the distributor later);
 *   - a combination whose behavior is not pinned down makes NO claim
 *     (Net Checkout on an organization-pays store is silent);
 *   - the exact sentence for each combination is pinned in
 *     __tests__/storePaymentSummary.spec.js.
 */
import {
  isCardPaymentMethod,
  isPayrollPaymentMethod,
} from "@/utils/storePaymentModel";

// Mirrors the allowance policy cadences the server accepts.
const CADENCE_PHRASE = {
  monthly: "each month",
  quarterly: "each quarter",
  annual: "each year",
  one_time: "in total",
};

const NET_CREDENTIAL_STORE_PASSWORD = "store_password";

function dollars(value) {
  const n = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(n) && n > 0 ? `$${n.toFixed(2)}` : "";
}

/**
 * "$25.00 each month", "$25.00" (cadence unknown) or "" (amount unknown).
 * An amount that is not a usable positive number is treated as unknown,
 * never as $0.
 */
function allowanceAmountPhrase(allowance) {
  const amount = dollars(allowance && allowance.amount);
  if (!amount) return "";
  const period = CADENCE_PHRASE[allowance.cadence];
  return period ? `${amount} ${period}` : amount;
}

/** The one-line allowance status shown next to the payment choice. */
export function allowanceLine(allowance) {
  if (!allowance) return "No shopper allowance.";
  const phrase = allowanceAmountPhrase(allowance);
  return phrase ? `Shopper allowance: ${phrase}.` : "Shopper allowance: on.";
}

/**
 * @param {object} settings
 * @param {string|null|undefined} settings.paymentMethod the EFFECTIVE method
 *   ("" when Accept Shopper Payments is off).
 * @param {object|null} [settings.allowance] the active allowance policy
 *   ({amount, cadence}), or null/undefined when there is none.
 * @param {boolean} [settings.netCheckout] Net Checkout is on AND saveable
 *   (not held waiting for a credential).
 * @param {string} [settings.netCredentialMode] "pin" | "store_password".
 * @returns {string} one paragraph.
 */
export function storePaymentSummary({
  paymentMethod,
  allowance = null,
  netCheckout = false,
  netCredentialMode = "pin",
} = {}) {
  const phrase = allowance ? allowanceAmountPhrase(allowance) : "";
  const parts = [];

  if (isCardPaymentMethod(paymentMethod)) {
    parts.push("Shoppers pay by card at checkout.");
    if (allowance) {
      parts.push(
        phrase
          ? `Each shopper's allowance (${phrase}) is applied first, and the shopper pays the rest by card.`
          : "Each shopper's allowance is applied first, and the shopper pays the rest by card."
      );
    }
    if (netCheckout) {
      const who =
        netCredentialMode === NET_CREDENTIAL_STORE_PASSWORD
          ? "Shoppers who entered the store password"
          : "Shoppers who have the Net Checkout PIN";
      parts.push(
        `${who} are offered, when Net Checkout is available, the option to place the order now and have it invoiced to your account later.`
      );
    }
  } else if (isPayrollPaymentMethod(paymentMethod)) {
    parts.push(
      "The organization pays. No card is taken at checkout: the client is billed, or the cost comes out of the shopper's pay."
    );
    if (allowance) {
      parts.push(
        phrase
          ? `Each shopper's allowance (${phrase}) is a firm limit: a shopper whose cart is over their remaining allowance cannot place the order.`
          : "Each shopper's allowance is a firm limit: a shopper whose cart is over their remaining allowance cannot place the order."
      );
    }
  } else {
    // Neither card nor the explicit payroll method: checkout refuses it
    // outright (storePaymentModel header; PAYMENTS_OFF_MESSAGE).
    parts.push(
      "Shoppers cannot check out: shopper payments are not turned on."
    );
  }

  return parts.join(" ");
}
